<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >

    <template v-slot:activator="{ props }">
      <slot name="activator" v-bind="{ props }" data-cy="podMenu-translateSubtitles-activator" />
    </template>

    <v-form ref="form" v-on:submit.prevent="runJobs" autocomplete="off">
      <v-card >
        <v-card-title class="d-flex justify-space-between">
          <span>{{$t('routes.pod.menuTranslateSubtitles.translateSubtitles')}}</span>
          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="scrollable mt-4">
          <div class="mb-4">
            <p>
             {{$t('routes.pod.menuTranslateSubtitles.selectedFile')}}:
            </p>
            <p class="font-weight-bold">
              {{ filename }}
            </p>
          </div>

          <div class="mb-6">
            <v-select
              v-model="selectedService"
              v-bind:rules="selectedServiceRules"
              v-bind:items="translationsServices"
              v-bind:label="$t('routes.pod.menuTranslateSubtitles.selectServiceToUse')"
              hide-details="auto"
              item-title="name"
              item-value="id"
              variant="solo-filled"
              flat required return-object single-line
              data-cy="podMenu-translateSubtitles-selectService"
              :list-props="{ tabindex: '0' }"
            />

            <div v-show="selectedService && selectedService.alias === 'aws-translate'">
              <div class="font-italic">
                <v-icon size="small">mdi-information-outline</v-icon>
                {{ $t('routes.pod.menuTranslateSubtitles.awsTranslateIsSlow') }}
              </div>
            </div>
          </div>


          <div class="mb-6">
            <v-select
              v-model="inputLanguage"
              v-bind:rules="inputLanguageRules"
              v-bind:items="languages"
              v-bind:label="$t('routes.pod.menuTranslateSubtitles.selectInputLanguage')"
              hide-details="auto"
              item-title="name"
              item-value="value"
              variant="solo-filled"
              :list-props="{ tabindex: '0' }"
              flat required return-object single-line
              data-cy="podMenu-translateSubtitles-selectInputLanguage"
            />
          </div>

          <div class="mb-6">
            <v-select
              v-model="outputLanguage"
              v-bind:rules="outputLanguageRules.concat([differentLanguagesRule])"
              v-bind:items="languages"
              v-bind:label="$t('routes.pod.menuTranslateSubtitles.selectOutputLanguage')"
              hide-details="auto"
              item-title="name"
              item-value="value"
              variant="solo-filled"
              :list-props="{ tabindex: '0' }"
              flat required return-object single-line
              data-cy="podMenu-translateSubtitles-selectOutputLanguage"
            />
          </div>

          <div class="d-flex font-italic">
            <div class="flex-grow-1">{{$t('routes.pod.menuTranslateSubtitles.numberOfCharacters')}}</div>
            <div>{{ toKilo(numberOfCharacters, 1) }}</div>
          </div>


          <div class="d-flex font-italic">
            <div class="flex-grow-1">{{$t('routes.pod.yourAvailableCredits')}}</div>
            <div>{{ creditsLeft }}</div>
          </div>

          <div class="d-flex font-italic">
            <div class="flex-grow-1">{{$t('routes.pod.serviceCost')}}</div>
            <div>{{ summedCost }}</div>
          </div>

          <div class="d-flex font-italic">
            <div class="flex-grow-1">{{$t('routes.pod.estimatedRemainingCredits')}}</div>
            <div>{{ creditsLeftAfterJob }}</div>
          </div>

          <hr />

          <!-- TODO: Rework this message box to redirect to URL -->
          <div v-if="creditsLeftAfterJob < 0" class="my-2">
            <template v-if="$canPurchaseResources()">
              <div>{{$t('routes.pod.menuTranslateSubtitles.wantToBuyBundle')}}</div>
              <div class="d-flex justify-space-around">
                <v-btn v-on:click="dialog = false" color="primary" elevation=0>{{$t('common.no')}}</v-btn>
                <v-btn @click="navigateToUsage" color="primary" elevation=0>{{$t('common.yes')}}</v-btn>
              </div>
            </template>
            <template v-else>
              <div class="bg-warning pa-2">{{$t('common.contactTenantAdminForCredits')}}</div>
            </template>
          </div>

          <UtilityPodsErrors data-cy="podMenu-translateSubtitles-utilityPodsError"></UtilityPodsErrors>

          <div v-show="successMessage" class="bg-success" data-cy="podMenu-translateSubtitles-successMessage">{{successMessage}}</div>
          <div v-show="errorMessage" class="bg-error" data-cy="podMenu-translateSubtitles-errorMessage">{{errorMessage}}</div>

          <div class="text-overflow">{{ assetName }}</div>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn
            v-if="creditsLeftAfterJob >= 0"
            v-bind:disabled="isRunJobsDisabled"
            type="submit"
            color="primary"
            elevation=0
            variant="flat"
            data-cy="podMenu-translateSubtitles-submit-btn"
          >
            {{$t('routes.pod.menuTranslateSubtitles.translateSubtitle')}}
          </v-btn>

          <v-btn
            variant="text"
            @click="dialog = false"
            data-cy="podMenu-translateSubtitles-close-btn"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useServiceStore } from '@/stores/ServiceStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { toKilo } from '@/components/formatters.js';
import DialogModel from '@/components/mixin/DialogModel.js';

export default {
  mixins: [DialogActionKeys, DialogModel],
  name: "TranslateSubtitleAssetDialog",
  props: {
    asset: {
      type: Object,
      required: false,
    }
  },
  data() {
    return {
      selectedService: null,
      selectedServiceRules: [
        v => !!v || this.$t('routes.pod.menuTranslateSubtitles.serviceProviderRequired'),
      ],

      inputLanguage: null,
      inputLanguageRules: [
        v => !!v || this.$t('routes.pod.menuTranslateSubtitles.inputLanguageRequired'),
      ],

      outputLanguage: null,
      outputLanguageRules: [
        v => !!v || this.$t('routes.pod.menuTranslateSubtitles.outputLanguageRequired'),
      ],

      serviceOptions: null,
      preventDoubleClick: false,

      errorMessage: '',
      successMessage: '',
    };
  },
  watch: {
    selectedService: function(newServices) {
      if (this.asset && this.inputLanguage && this.outputLanguage) {
        this.updatePodsUtility();
        this.errorMessage = '';
      }

      if (newServices && newServices.alias) {
        this.refreshServiceOptions(newServices.alias);
      }
    },
    // inputSource: function() {
    //   if (this.selectedService && this.inputLanguage && this.outputLanguage) {
    //     this.updatePodsUtility();
    //     this.errorMessage = '';
    //   }
    // },
    inputLanguage: function() {
      if (this.selectedService && this.asset && this.outputLanguage) {
        this.updatePodsUtility();
        this.errorMessage = '';
      }
    },
    outputLanguage: function() {
      if (this.selectedService && this.inputLanguage && this.asset) {
        this.updatePodsUtility();
        this.errorMessage = '';
      }
    }

  },
  methods: {
    toKilo,
    navigateToUsage() {
      this.dialog = false;
      this.$router.push('/console/usage');
    },
    refreshServiceOptions: function(serviceAlias) {
      let self = this;
      const serviceStore = useServiceStore();
      serviceStore.refreshServiceOptions(serviceAlias)
      .then(function(options) {
        self.serviceOptions = options;
      });
    },
    updatePodsUtility() {
      if (this.selectedSuitablePods.length === 0) {
        const serviceStore = useServiceStore();
        serviceStore.resetUtilityPods();
        return;
      }

      if (!this.selectedService || !this.asset || !this.inputLanguage || !this.outputLanguage || this.inputLanguage.value === this.outputLanguage.value) {
        return;
      }

      const payload = {
        serviceAlias: this.selectedService.alias,
        pods: this.selectedSuitablePods,
        assetID: this.asset.id,
        sourcelanguage: this.inputLanguage.value,
        targetlanguage: this.outputLanguage.value
      };

      const serviceStore = useServiceStore();
      return serviceStore.updatePodsUtility(payload);
    },
    runJobs: async function() {
      console.log("Run Translation job!");
      console.log("Pods:", this.selectedSuitablePods);
      console.log("Service:", this.selectedService);
      console.log("Input source: ", this.asset);
      console.log("Input language: ", this.inputLanguage);

      this.errorMessage = '';
      this.successMessage = '';

      const validForm = await this.$refs.form.validate();
      if (!validForm.valid) {
        return;
      }

      const payload = {
        serviceAlias: this.selectedService.alias,
        utilityPods: this.runnablePods,
        params: {
          assetid: this.asset.id,
          sourcelanguage: this.inputLanguage.value,
          targetlanguage: this.outputLanguage.value
        }
      };

      this.preventDoubleclick = true;
      setTimeout(() => {
        this.preventDoubleclick = false;
      }, 10000);

      try {
        await this.updatePodsUtility();

        const serviceStore = useServiceStore();
        const response = await serviceStore.runService(payload);

        if (response?.pods) {
          for (const pod of response.pods) {
            if (pod.error) {
              this.errorMessage = this.$replaceT(pod.error.code, pod.error.params, pod.error.content);
              break;
            }
          }
        }

        if (!this.utilityPodsErrors || this.utilityPodsErrors.length == 0) {
          this.successMessage = this.$t("routes.pod.menuTranslateSubtitles.operationQueued");
        }
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error && error.response.data.error.message) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }

        this.preventDoubleclick = false;
      }
    },
  },
  computed: {
    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    isAudioPod: function() {
      const podStore = usePodStore();
      return podStore.isAudioPod;
    },
    selectedSuitablePods: function() {
      if (this.isVideoPod || this.isAudioPod) {
        return [this.pod];
      }
      return [];
      // return this.selectedVideoPods.concat(this.selectedAudioPods);
    },
    translationsServices: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getTranslations;
    },
    languages: function() {
      if (this.selectedService && this.serviceOptions && this.serviceOptions.languages) {
        return this.serviceOptions.languages;
      }
      return [];
    },
    differentLanguagesRule: function() {
      if (this.inputLanguage && this.outputLanguage && this.inputLanguage.value === this.outputLanguage.value) {
        return this.$t('routes.pod.menuTranslateSubtitles.theLanguagesMustDiffer');
      }
      return true;
    },
    runnablePods: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getRunnablePods;
    },
    getUtilityPods: function() {
      const serviceStore = useServiceStore();
      return serviceStore.getUtilityPods;
    },
    summedCost: function() {
      let sum = 0;
      for (let i = 0; i < this.getUtilityPods.length; i++) {
        if (this.getUtilityPods[i].metadata &&
            this.getUtilityPods[i].metadata.cost !== null &&
            this.getUtilityPods[i].metadata.cost !== undefined) {
          sum += this.getUtilityPods[i].metadata.cost;
        }
      }
      return sum;
    },
    numberOfCharacters: function() {
      if (!Array.isArray(this.getUtilityPods)) {
        return 0;
      }

      let characters = 0;
      for(let i = 0; i < this.getUtilityPods.length; i++) {
        if (this.getUtilityPods[i].metadata &&
            this.getUtilityPods[i].metadata.characters !== null &&
            this.getUtilityPods[i].metadata.characters !== undefined) {
          characters += this.getUtilityPods[i].metadata.characters;
        }
      }

      return characters;
    },
    creditsLeft: function() {
      for(let i = 0; i < this.getUtilityPods.length; i++) {
        if (this.getUtilityPods[i].metadata &&
            this.getUtilityPods[i].metadata.available !== null &&
            this.getUtilityPods[i].metadata.available !== undefined) {
          return this.getUtilityPods[i].metadata.available;
        }
      }

      return 0;
    },
    creditsLeftAfterJob: function() {
      return this.creditsLeft - this.summedCost;
    },
    isRunJobsDisabled: function() {
      if (!this.selectedService ||
          this.runnablePods.length === 0 ||
          !this.asset ||
          !this.inputLanguage ||
          !this.outputLanguage ||
          this.preventDoubleClick
      ) {
        return true;
      }

      return false;
    },
    assetName: function() {
      return this.asset?.name ?? '';
    },
    filename: function () {
      if (this.asset && this.asset.name) {
        if (this.asset.name.length > 20) {
          return this.asset.name.substring(0, 20) + '...';
        }

        return this.asset.name;
      }

      return '';
    },
  }
}
</script>

<style>
</style>
